import { useCallback, useEffect, useRef, useState } from 'react'

export type LoadState<T> = {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * The console's one data-loading shape: `{data, loading, error, reload}` for a call that re-runs
 * on demand. `load` must be `useCallback`-stable — `reload` bumps a nonce that re-runs the effect,
 * so a caller that recreates `load` every render would loop.
 */
export function useLoad<T>(
  load: () => Promise<T>,
  describe: (caught: unknown) => string,
): LoadState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)
  const describeRef = useRef(describe)
  describeRef.current = describe

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    load().then(
      (value) => {
        if (!active) return
        setData(value)
        setLoading(false)
      },
      (caught: unknown) => {
        if (!active) return
        setError(describeRef.current(caught))
        setLoading(false)
      },
    )
    return () => {
      active = false
    }
  }, [load, nonce])

  const reload = useCallback(() => setNonce((value) => value + 1), [])
  return { data, loading, error, reload }
}
